import React, { useState, useEffect, useMemo } from 'react';
import { auth, db, isQuotaError } from '../firebase';
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Edit2, 
  ChevronRight, 
  Users, 
  Bus, 
  Activity, 
  Download, 
  FileText, 
  Table as TableIcon,
  Save,
  X,
  Check,
  Search,
  Clock,
  Loader2,
  UserCheck,
  UserX,
  AlertTriangle,
  Phone,
  Mail,
  CheckSquare,
  Settings,
  Lock
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'motion/react';
import { syncHolidayToRegular, syncHolidayTransportBatchToRegular } from '../services/attendanceSyncService';

interface Holiday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  responsible: string;
}

interface HolidayActivity {
  id: string;
  holidayId: string;
  date: string;
  type: 'in-school' | 'out-of-school';
  activityName: string;
  operator: string;
  transporterName?: string;
  transportCount?: number;
}

interface HolidayAttendance {
  id: string;
  holidayId: string;
  date: string;
  targetId: string;
  targetType: 'student' | 'educational' | 'assistant' | 'substitute' | 'transporter' | 'transportLine';
  status: 'present' | 'absent';
  hours?: number;
}

interface Student {
  id: string;
  fullName: string;
  className: string;
  transportName: string;
  classId: string;
  active?: boolean;
  fatherName?: string;
  fatherPhone?: string;
  motherName?: string;
  motherPhone?: string;
}

interface StaffMember {
  id: string;
  fullName: string;
  nationalId: string;
  type: 'educational' | 'assistant';
  role?: string;
  active?: boolean;
  isSubstitute?: boolean;
}

interface Substitute {
  id: string;
  firstName: string;
  lastName: string;
  active?: boolean;
}

interface TransportLine {
  id: string;
  name: string;
  driverName?: string;
}

const getDaysArray = (start: string, end: string, includeWeekends = false) => {
  const arr = [];
  // Use noon to avoid any timezone shifting issues when calling getDay()
  const startDate = new Date(start + 'T12:00:00');
  const endDate = new Date(end + 'T12:00:00');
  
  for (let dt = new Date(startDate); dt <= endDate; dt.setDate(dt.getDate() + 1)) {
    const day = dt.getDay();
    // 5 is Friday, 6 is Saturday
    if (includeWeekends || (day !== 5 && day !== 6)) {
      arr.push(dt.toISOString().split('T')[0]);
    }
  }
  return arr;
};

export default function Holidays() {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [isAddingHoliday, setIsAddingHoliday] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ name: '', startDate: '', endDate: '', responsible: '' });
  const [activeSubTab, setActiveSubTab] = useState<'dashboard' | 'activities' | 'students' | 'staff' | 'transport'>('dashboard');
  const [selectedDate, setSelectedDate] = useState<string>('');
  
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [exportType, setExportType] = useState<'pdf' | 'excel' | 'mail'>('pdf');
  const [exportFilter, setExportFilter] = useState<'all' | 'present'>('all');
  const [exportDateRange, setExportDateRange] = useState<'all' | 'custom'>('all');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [targetEmail, setTargetEmail] = useState('');
  
  // Data for attendance
  const [students, setStudents] = useState<Student[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [substitutes, setSubstitutes] = useState<Substitute[]>([]);
  const [transportLines, setTransportLines] = useState<TransportLine[]>([]);
  const [activities, setActivities] = useState<HolidayActivity[]>([]);
  const [attendance, setAttendance] = useState<HolidayAttendance[]>([]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'holidays'), (snap) => {
      setHolidays(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Holiday)));
    }, (error) => {
      if (isQuotaError(error)) {
        console.warn("Holidays fetch paused: Quota exceeded");
        unsub();
      } else {
        console.error("Error fetching holidays:", error);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (selectedHoliday) {
      // Only fetch these when a holiday is selected to save reads
      const unsubStudents = onSnapshot(collection(db, 'students'), (snap) => {
        setStudents(snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as Student))
          .filter(s => s.active !== false)
        );
      }, (error) => {
        if (isQuotaError(error)) {
          console.warn("Students fetch paused: Quota exceeded");
          unsubStudents();
        } else console.error("Error fetching students:", error);
      });
      const unsubStaff = onSnapshot(collection(db, 'staff'), (snap) => {
        const staffData = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as StaffMember))
          .filter(s => s.active !== false);
        setStaff(staffData.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'he')));
      }, (error) => {
        if (isQuotaError(error)) {
          console.warn("Staff fetch paused: Quota exceeded");
          unsubStaff();
        } else console.error("Error fetching staff:", error);
      });
      const unsubSubs = onSnapshot(collection(db, 'substitutes'), (snap) => {
        const subsData = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as Substitute))
          .filter(s => s.active !== false);
        setSubstitutes(subsData.sort((a, b) => ((a.firstName || '') + ' ' + (a.lastName || '')).localeCompare((b.firstName || '') + ' ' + (b.lastName || ''), 'he')));
      }, (error) => {
        if (isQuotaError(error)) {
          console.warn("Substitutes fetch paused: Quota exceeded");
          unsubSubs();
        } else console.error("Error fetching substitutes:", error);
      });
      const unsubTransport = onSnapshot(collection(db, 'transportLines'), (snap) => {
        setTransportLines(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as TransportLine)));
      }, (error) => {
        if (isQuotaError(error)) {
          console.warn("Transport fetch paused: Quota exceeded");
          unsubTransport();
        } else console.error("Error fetching transport:", error);
      });

      const unsubActivities = onSnapshot(
        query(collection(db, 'holidayActivities'), where('holidayId', '==', selectedHoliday.id)),
        (snap) => {
          setActivities(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as HolidayActivity)));
        }, (error) => {
          if (isQuotaError(error)) {
            console.warn("Activities fetch paused: Quota exceeded");
            unsubActivities();
          } else console.error("Error fetching activities:", error);
        }
      );
      const unsubAttendance = onSnapshot(
        query(collection(db, 'holidayAttendance'), where('holidayId', '==', selectedHoliday.id)),
        (snap) => {
          setAttendance(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as HolidayAttendance)));
        }, (error) => {
          if (isQuotaError(error)) {
            console.warn("Attendance fetch paused: Quota exceeded");
            unsubAttendance();
          } else console.error("Error fetching attendance:", error);
        }
      );
      
      // Set initial selected date to today if within range, else holiday start date
      const today = new Date().toISOString().split('T')[0];
      if (today >= selectedHoliday.startDate && today <= selectedHoliday.endDate) {
        setSelectedDate(today);
      } else {
        setSelectedDate(selectedHoliday.startDate);
      }
      
      return () => {
        unsubStudents();
        unsubStaff();
        unsubSubs();
        unsubTransport();
        unsubActivities();
        unsubAttendance();
      };
    }
  }, [selectedHoliday]);

  const handleAddHoliday = async () => {
    if (!newHoliday.name || !newHoliday.startDate || !newHoliday.endDate) return;
    await addDoc(collection(db, 'holidays'), {
      ...newHoliday,
      createdAt: serverTimestamp()
    });
    setIsAddingHoliday(false);
    setNewHoliday({ name: '', startDate: '', endDate: '', responsible: '' });
  };

  const handleExport = async (category: string | 'all') => {
    if (!selectedHoliday) return;
    
    const startDate = exportDateRange === 'custom' && exportStartDate ? exportStartDate : selectedHoliday.startDate;
    const endDate = exportDateRange === 'custom' && exportEndDate ? exportEndDate : selectedHoliday.endDate;
    
    const dates = getDaysArray(startDate, endDate);
    
    const categoriesToExport = category === 'all' 
      ? ['activities', 'students', 'educational', 'assistant', 'substitute', 'transport']
      : [category];

    if (exportType === 'mail') {
      let email = targetEmail;
      if (!email || !email.includes('@')) {
        email = prompt('אנא הכנס כתובת מייל למשלוח הדוח:', targetEmail) || '';
      }
      
      if (!email || !email.includes('@')) {
        alert('אנא הכנס כתובת מייל תקינה');
        return;
      }
      
      setIsGenerating(true);
      try {
        // Generate files locally for the user to see them
        await generatePDFReport(categoriesToExport, dates);
        generateExcelReport(categoriesToExport, dates);
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        alert(`הדוח (PDF + Excel) נשלח בהצלחה לכתובת: ${email}`);
        setTargetEmail(email);
      } catch (error) {
        console.error('Error sending mail:', error);
        alert('שגיאה בשליחת המייל.');
      } finally {
        setIsGenerating(false);
      }
    } else if (exportType === 'pdf') {
      setIsGenerating(true);
      try {
        await generatePDFReport(categoriesToExport, dates);
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('שגיאה בהפקת ה-PDF. נסה שוב או השתמש ב-Excel.');
      } finally {
        setIsGenerating(false);
      }
    } else {
      generateExcelReport(categoriesToExport, dates);
    }
    setIsExportModalOpen(false);
  };

  const generatePDFReport = async (categories: string[], dates: string[]) => {
    const doc = new jsPDF('l', 'pt', 'a4');
    let fontName = 'helvetica';
    
    // Load Hebrew fonts (Assistant Regular and Bold)
    try {
      const regularUrl = 'https://cdn.jsdelivr.net/gh/googlefonts/assistant@master/fonts/ttf/Assistant-Regular.ttf';
      const boldUrl = 'https://cdn.jsdelivr.net/gh/googlefonts/assistant@master/fonts/ttf/Assistant-Bold.ttf';
      
      const [regRes, boldRes] = await Promise.all([fetch(regularUrl), fetch(boldUrl)]);
      
      if (!regRes.ok || !boldRes.ok) throw new Error('Font fetch failed');
      
      const [regBuffer, boldBuffer] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
      
      const toBase64 = (buffer: ArrayBuffer) => btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      
      doc.addFileToVFS('Assistant-Regular.ttf', toBase64(regBuffer));
      doc.addFont('Assistant-Regular.ttf', 'Assistant', 'normal');
      
      doc.addFileToVFS('Assistant-Bold.ttf', toBase64(boldBuffer));
      doc.addFont('Assistant-Bold.ttf', 'Assistant', 'bold');
      
      doc.setFont('Assistant', 'normal');
      fontName = 'Assistant';
    } catch (e) {
      console.warn('Could not load Hebrew font, falling back to helvetica', e);
      fontName = 'helvetica';
      doc.setFont(fontName);
    }

    // Robust Hebrew reversal for jspdf
    const reverseHebrew = (text: string) => {
      if (!text || typeof text !== 'string') return text;
      if (/^[0-9VvXx\-\/.:\s]+$/.test(text)) return text;

      // Split by spaces to keep word order but reverse characters in Hebrew words
      // However, for jsPDF with align:right/center, we often need to reverse the whole line
      // and then fix the non-Hebrew parts.
      
      const reversed = text.split('').reverse().map(c => {
        if (c === '(') return ')';
        if (c === ')') return '(';
        if (c === '[') return ']';
        if (c === ']') return '[';
        if (c === '{') return '}';
        if (c === '}') return '{';
        return c;
      }).join('');

      // Fix numbers, dates and English words
      // We also include common separators like colons and dashes if they are between numbers/English
      return reversed.replace(/([a-zA-Z0-9\-\/.:]{2,})/g, match => {
        return match.split('').reverse().join('');
      });
    };

    categories.forEach((category, index) => {
      if (index > 0) doc.addPage();

      const pageWidth = doc.internal.pageSize.getWidth();
      const title = `דוח חופשה: ${selectedHoliday?.name} - ${getCategoryLabel(category)}`;
      const subtitle = `תאריכים: ${selectedHoliday?.startDate} עד ${selectedHoliday?.endDate}`;
      
      doc.setFont(fontName, 'bold');
      doc.setFontSize(24);
      doc.setTextColor(30, 41, 59); // slate-800
      doc.text(reverseHebrew(title), pageWidth / 2, 50, { align: 'center' });
      
      doc.setFont(fontName, 'normal');
      doc.setFontSize(14);
      doc.setTextColor(100, 116, 139); // slate-500
      doc.text(reverseHebrew(subtitle), pageWidth / 2, 75, { align: 'center' });
      
      // Reset color for table
      doc.setTextColor(0, 0, 0);

      let head: string[][] = [];
      let body: any[][] = [];

      const formatDateShort = (dateStr: string) => {
        if (!dateStr || !dateStr.includes('-')) return dateStr;
        const parts = dateStr.split('-');
        return `${parts[2]}/${parts[1]}`;
      };

      if (category === 'activities') {
        head = [['כמות', 'מסיע', 'מפעיל', 'פעילות', 'סוג', 'תאריך'].map(reverseHebrew)];
        body = activities.sort((a, b) => a.date.localeCompare(b.date)).map(a => [
          a.transportCount || '-',
          reverseHebrew(a.transporterName || '-'),
          reverseHebrew(a.operator),
          reverseHebrew(a.activityName),
          reverseHebrew(a.type === 'in-school' ? 'תוך בית ספרית' : 'חוץ בית ספרית'),
          a.date
        ]);
      } else if (category === 'students') {
        const reversedDates = [...dates].reverse();
        head = [[...reversedDates.map(formatDateShort), 'כיתה', 'שם תלמיד'].map(reverseHebrew)];
        
        const allRows = students
          .sort((a, b) => a.className.localeCompare(b.className, 'he') || a.fullName.localeCompare(b.fullName, 'he'))
          .map(s => {
            const dateColumns: string[] = [];
            let isPresentAtLeastOnce = false;
            reversedDates.forEach(date => {
              const record = attendance.find(a => a.targetId === s.id && a.date === date && a.targetType === 'student');
              const status = record ? (record.status === 'present' ? 'V' : 'X') : '-';
              if (status === 'V') isPresentAtLeastOnce = true;
              dateColumns.push(status);
            });
            return {
              data: [...dateColumns, reverseHebrew(s.className), reverseHebrew(s.fullName)],
              isPresent: isPresentAtLeastOnce
            };
          });

        const filteredRows = exportFilter === 'present' ? allRows.filter(r => r.isPresent) : allRows;
        body = filteredRows.map(r => r.data);
      } else if (category === 'educational' || category === 'assistant' || category === 'substitute') {
        const reversedDates = [...dates].reverse();
        const dateHeaders = reversedDates.flatMap(d => ['שעות', formatDateShort(d)]);
        head = [[...dateHeaders, 'תפקיד', 'שם איש צוות'].map(reverseHebrew)];
        
        const items = category === 'substitute' 
          ? substitutes.map(s => ({ id: s.id, name: `${s.firstName} ${s.lastName}`, info: s.type === 'educational' ? 'חינוכי' : 'סייע/ת', type: 'substitute' }))
          : staff.filter(s => s.type === category).map(s => ({ id: s.id, name: s.fullName, info: s.role, type: s.type }));
        
        const allRows = items.map(item => {
          const dateColumns: any[] = [];
          let isPresentAtLeastOnce = false;
          reversedDates.forEach(date => {
            const record = attendance.find(a => a.targetId === item.id && a.date === date && a.targetType === (category === 'substitute' ? 'substitute' : category));
            const status = record ? (record.status === 'present' ? 'V' : 'X') : '-';
            if (status === 'V') isPresentAtLeastOnce = true;
            dateColumns.push(record?.hours || 0);
            dateColumns.push(status);
          });
          return {
            data: [...dateColumns, reverseHebrew(item.info || ''), reverseHebrew(item.name)],
            isPresent: isPresentAtLeastOnce
          };
        });

        const filteredRows = exportFilter === 'present' ? allRows.filter(r => r.isPresent) : allRows;
        body = filteredRows.map(r => r.data);
      } else if (category === 'transport') {
        const reversedDates = [...dates].reverse();
        head = [[...reversedDates.map(formatDateShort), 'סוג', 'שם קו/מסיע'].map(reverseHebrew)];
        const lines = transportLines.map(l => ({ id: l.id, name: l.name, type: 'transportLine' }));
        const external = Array.from(new Set(activities.map(a => a.transporterName).filter(Boolean)))
          .map(name => ({ id: name!, name: name!, type: 'transporter' }));
        
        const allRows = [...lines, ...external].map(item => {
          const dateColumns: string[] = [];
          let isPresentAtLeastOnce = false;
          reversedDates.forEach(date => {
            const record = attendance.find(a => a.targetId === item.id && a.date === date && a.targetType === item.type);
            const status = record ? (record.status === 'present' ? 'V' : 'X') : '-';
            if (status === 'V') isPresentAtLeastOnce = true;
            dateColumns.push(status);
          });
          return {
            data: [...dateColumns, reverseHebrew(item.type === 'transportLine' ? 'קו רגיל' : 'מסיע חוץ'), reverseHebrew(item.name)],
            isPresent: isPresentAtLeastOnce
          };
        });

        const filteredRows = exportFilter === 'present' ? allRows.filter(r => r.isPresent) : allRows;
        body = filteredRows.map(r => r.data);
      }

      const columnCount = head[0].length;
      let fontSize = columnCount > 20 ? 6 : columnCount > 12 ? 7 : 9;
      let dateCellWidth = columnCount > 20 ? 25 : 30;
      
      let columnStyles: any = {};
      
      if (category === 'activities') {
        columnStyles = {
          3: { cellWidth: 120, font: fontName, fontStyle: 'bold' }, // Activity Name
          2: { cellWidth: 80 }, // Operator
          1: { cellWidth: 80 }, // Transporter
        };
      } else {
        const nameColumnIndex = columnCount - 1;
        const infoColumnIndex = columnCount - 2;
        columnStyles = {
          [nameColumnIndex]: { cellWidth: 100, font: fontName, fontStyle: 'bold' },
          [infoColumnIndex]: { cellWidth: 60 },
        };
      }

      autoTable(doc, {
        startY: 90,
        head: head,
        body: body,
        styles: { 
          font: fontName, 
          halign: 'right',
          fontSize: fontSize,
          cellPadding: 2,
          overflow: 'linebreak'
        },
        columnStyles: columnStyles,
        headStyles: { 
          fillColor: [79, 70, 229],
          halign: 'right',
          font: fontName,
          fontStyle: 'bold',
          fontSize: fontSize,
        },
        didParseCell: (data) => {
          // Make date columns narrow if they only contain V/X/- (only for attendance categories)
          if (category !== 'activities' && data.column.index < columnCount - 2) {
            data.cell.styles.cellWidth = dateCellWidth;
            data.cell.styles.halign = 'center';
          }
          
          if (data.section === 'body') {
            const rowData = body[data.row.index];
            // Check if any cell in the row has 'V' (Present)
            const isPresent = rowData.some(cell => cell === 'V');
            if (isPresent) {
              data.cell.styles.fillColor = [220, 252, 231]; // Light green
            } else if (rowData.some(cell => cell === 'X')) {
              data.cell.styles.fillColor = [254, 226, 226]; // Light red
            }
          }
        }
      });
    });

    doc.save(`${selectedHoliday?.name}_report.pdf`);
  };

  const generateExcelReport = (categories: string[], dates: string[]) => {
    const wb = XLSX.utils.book_new();

    categories.forEach(category => {
      let data: any[] = [];

      if (category === 'activities') {
        data = activities.sort((a, b) => a.date.localeCompare(b.date)).map(a => ({
          'תאריך': a.date,
          'סוג': a.type === 'in-school' ? 'תוך בית ספרית' : 'חוץ בית ספרית',
          'פעילות': a.activityName,
          'מפעיל': a.operator,
          'מסיע': a.transporterName || '-',
          'כמות הסעות': a.transportCount || '-'
        }));
      } else if (category === 'students') {
        data = students
          .sort((a, b) => a.className.localeCompare(b.className, 'he') || a.fullName.localeCompare(b.fullName, 'he'))
          .map(s => {
            const row: any = { 'שם תלמיד': s.fullName, 'כיתה': s.className };
            let isPresentAtLeastOnce = false;
            dates.forEach(date => {
              const record = attendance.find(a => a.targetId === s.id && a.date === date && a.targetType === 'student');
              const status = record ? (record.status === 'present' ? 'V' : 'X') : '-';
              if (status === 'V') isPresentAtLeastOnce = true;
              row[date] = status;
            });
            return { row, isPresent: isPresentAtLeastOnce };
          })
          .filter(item => exportFilter === 'all' || item.isPresent)
          .map(item => item.row);
      } else if (category === 'educational' || category === 'assistant' || category === 'substitute') {
        const items = category === 'substitute' 
          ? substitutes.map(s => ({ id: s.id, name: `${s.firstName} ${s.lastName}`, info: s.type === 'educational' ? 'חינוכי' : 'סייע/ת', type: 'substitute' }))
          : staff.filter(s => s.type === category).map(s => ({ id: s.id, name: s.fullName, info: s.role, type: s.type }));
        
        data = items.map(item => {
          const row: any = { 'שם איש צוות': item.name, 'תפקיד': item.info };
          let isPresentAtLeastOnce = false;
          dates.forEach(date => {
            const record = attendance.find(a => a.targetId === item.id && a.date === date && a.targetType === (category === 'substitute' ? 'substitute' : category));
            const status = record ? (record.status === 'present' ? 'V' : 'X') : '-';
            if (status === 'V') isPresentAtLeastOnce = true;
            row[`${date} - נוכחות`] = status;
            row[`${date} - שעות`] = record?.hours || 0;
          });
          return { row, isPresent: isPresentAtLeastOnce };
        })
        .filter(item => exportFilter === 'all' || item.isPresent)
        .map(item => item.row);
      } else if (category === 'transport') {
        const lines = transportLines.map(l => ({ id: l.id, name: l.name, type: 'transportLine' }));
        const external = Array.from(new Set(activities.map(a => a.transporterName).filter(Boolean)))
          .map(name => ({ id: name!, name: name!, type: 'transporter' }));
        
        data = [...lines, ...external].map(item => {
          const row: any = { 'שם קו/מסיע': item.name, 'סוג': item.type === 'transportLine' ? 'קו רגיל' : 'מסיע חוץ' };
          let isPresentAtLeastOnce = false;
          dates.forEach(date => {
            const record = attendance.find(a => a.targetId === item.id && a.date === date && a.targetType === item.type);
            const status = record ? (record.status === 'present' ? 'V' : 'X') : '-';
            if (status === 'V') isPresentAtLeastOnce = true;
            row[date] = status;
          });
          return { row, isPresent: isPresentAtLeastOnce };
        })
        .filter(item => exportFilter === 'all' || item.isPresent)
        .map(item => item.row);
      }

      if (data.length > 0) {
        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, getCategoryLabel(category));
      }
    });

    XLSX.writeFile(wb, `${selectedHoliday?.name}_report.xlsx`);
  };

  const getCategoryLabel = (cat: string) => {
    const labels: any = {
      activities: 'פעילויות',
      students: 'תלמידים',
      educational: 'צוות חינוכי טיפולי',
      assistant: 'סייעות',
      substitute: 'מילוי מקום',
      transport: 'קווי הסעות'
    };
    return labels[cat] || cat;
  };

  if (selectedHoliday) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6" dir="rtl">
        <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSelectedHoliday(null)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{selectedHoliday.name}</h1>
              <p className="text-slate-500">{selectedHoliday.startDate} - {selectedHoliday.endDate}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => { setExportType('pdf'); setIsExportModalOpen(true); }} 
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              <FileText size={18} />
              <span>PDF</span>
            </button>
            <button 
              onClick={() => { setExportType('excel'); setIsExportModalOpen(true); }} 
              className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
            >
              <TableIcon size={18} />
              <span>Excel</span>
            </button>
            <button 
              onClick={() => { setExportType('mail'); setIsExportModalOpen(true); }} 
              className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <Mail size={18} />
              <span>שלח למייל</span>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isExportModalOpen && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl overflow-hidden"
              >
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">בחר קטגוריה ליצוא ({exportType.toUpperCase()})</h3>
                  <button onClick={() => setIsExportModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X size={20} />
                  </button>
                </div>
                <div className="p-6 grid grid-cols-1 gap-4 max-h-[80vh] overflow-y-auto">
                  {isGenerating ? (
                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                      <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-slate-600 font-medium">
                        {exportType === 'mail' ? 'שולח נתונים למייל...' : 'מייצר דוח PDF עם תמיכה בעברית...'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200 shadow-sm">
                        <button 
                          onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-bold text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-all border border-transparent hover:border-indigo-100"
                        >
                          <Settings size={18} className={isSettingsExpanded ? 'rotate-90 transition-transform' : 'transition-transform'} />
                          {isSettingsExpanded ? 'סגור הגדרות' : 'הגדרות יצוא וסינון'}
                        </button>
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-2 text-slate-500 text-[11px] font-bold">
                            <Lock size={12} className="text-slate-400" />
                            <span>הגדרות פעילות (נעולות)</span>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                            <span>סוג: <span className="text-indigo-600 font-bold">{exportType.toUpperCase()}</span></span>
                            <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                            <span>טווח: <span className="text-indigo-600 font-bold">{exportDateRange === 'all' ? 'הכל' : 'מותאם'}</span></span>
                            <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                            <span>סינון: <span className="text-indigo-600 font-bold">{exportFilter === 'all' ? 'הכל' : 'נוכחים'}</span></span>
                          </div>
                        </div>
                      </div>

                      <AnimatePresence>
                        {isSettingsExpanded && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-4 mb-6 p-4 bg-white rounded-xl border border-slate-100 shadow-inner">
                              {exportType === 'mail' && (
                                <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                                  <label className="block text-sm font-bold text-indigo-900 mb-2 text-right">
                                    כתובת אימייל למשלוח:
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="email"
                                      value={targetEmail}
                                      onChange={(e) => setTargetEmail(e.target.value)}
                                      placeholder="example@mail.com"
                                      className="w-full px-4 py-2 rounded-lg border border-indigo-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-left font-medium"
                                      dir="ltr"
                                    />
                                    <Mail className="absolute left-3 top-2.5 text-indigo-400" size={18} />
                                  </div>
                                </div>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                  <label className="block text-sm font-bold text-slate-700 mb-2 text-right">
                                    טווח תאריכים:
                                  </label>
                                  <div className="flex gap-2 mb-3">
                                    <button
                                      onClick={() => setExportDateRange('all')}
                                      className={`flex-1 py-1.5 rounded-lg border transition-all text-[11px] font-bold ${
                                        exportDateRange === 'all' ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                                      }`}
                                    >
                                      כל ימי הקייטנה
                                    </button>
                                    <button
                                      onClick={() => {
                                        setExportDateRange('custom');
                                        if (!exportStartDate) setExportStartDate(selectedHoliday?.startDate || '');
                                        if (!exportEndDate) setExportEndDate(selectedHoliday?.endDate || '');
                                      }}
                                      className={`flex-1 py-1.5 rounded-lg border transition-all text-[11px] font-bold ${
                                        exportDateRange === 'custom' ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                                      }`}
                                    >
                                      טווח מותאם
                                    </button>
                                  </div>
                                  
                                  {exportDateRange === 'custom' && (
                                    <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2">
                                      <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">מתאריך:</label>
                                        <input 
                                          type="date" 
                                          value={exportStartDate}
                                          onChange={e => setExportStartDate(e.target.value)}
                                          min={selectedHoliday?.startDate}
                                          max={selectedHoliday?.endDate}
                                          className="w-full px-2 py-1 text-[10px] rounded-lg border border-slate-200 outline-none font-bold"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">עד תאריך:</label>
                                        <input 
                                          type="date" 
                                          value={exportEndDate}
                                          onChange={e => setExportEndDate(e.target.value)}
                                          min={selectedHoliday?.startDate}
                                          max={selectedHoliday?.endDate}
                                          className="w-full px-2 py-1 text-[10px] rounded-lg border border-slate-200 outline-none font-bold"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                  <label className="block text-sm font-bold text-slate-700 mb-2 text-right">
                                    סינון נתונים:
                                  </label>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => setExportFilter('all')}
                                      className={`flex-1 py-1.5 rounded-lg border transition-all text-[11px] font-bold ${
                                        exportFilter === 'all' ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                                      }`}
                                    >
                                      הכל
                                    </button>
                                    <button
                                      onClick={() => setExportFilter('present')}
                                      className={`flex-1 py-1.5 rounded-lg border transition-all text-[11px] font-bold ${
                                        exportFilter === 'present' ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                                      }`}
                                    >
                                      נוכחים בלבד
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => handleExport('all')}
                          className="col-span-2 flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50 hover:border-indigo-600 hover:bg-indigo-100 transition-all text-right group"
                        >
                          <div className="p-2 bg-indigo-600 text-white rounded-lg">
                            <CheckSquare size={20} />
                          </div>
                          <span className="font-bold text-indigo-700">בחר הכל (כל הקטגוריות)</span>
                        </button>
                        
                        {[
                          { id: 'activities', label: 'פעילויות', icon: Activity },
                          { id: 'students', label: 'תלמידים', icon: Users },
                          { id: 'educational', label: 'צוות חינוכי טיפולי', icon: Clock },
                          { id: 'assistant', label: 'סייעות', icon: Clock },
                          { id: 'substitute', label: 'מילוי מקום', icon: Clock },
                          { id: 'transport', label: 'קווי הסעות', icon: Bus },
                        ].map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => handleExport(cat.id)}
                            className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 hover:border-indigo-600 hover:bg-indigo-50 transition-all text-right group"
                          >
                            <div className="p-2 bg-slate-100 text-slate-600 rounded-lg group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                              <cat.icon size={18} />
                            </div>
                            <span className="text-xs font-medium text-slate-700 group-hover:text-indigo-600">{cat.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', label: 'דשבורד סיכום', icon: Activity },
            { id: 'activities', label: 'פעילויות', icon: Calendar },
            { id: 'students', label: 'נוכחות תלמידים', icon: Users },
            { id: 'staff', label: 'נוכחות צוות', icon: Clock },
            { id: 'transport', label: 'הסעות', icon: Bus },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeSubTab === tab.id 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-bottom border-slate-100 bg-slate-50 flex gap-2 overflow-x-auto">
            {getDaysArray(selectedHoliday.startDate, selectedHoliday.endDate).map(date => (
              <button
                key={date}
                onClick={() => setSelectedDate(date)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedDate === date 
                  ? 'bg-white text-indigo-600 shadow-sm border border-slate-200' 
                  : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {new Date(date).toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'numeric' })}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeSubTab === 'dashboard' && (
              <HolidayDashboard 
                holidayId={selectedHoliday.id}
                date={selectedDate}
                students={students}
                staff={staff}
                attendance={attendance.filter(a => a.date === selectedDate)}
              />
            )}
            {activeSubTab === 'activities' && (
              <ActivitySection 
                holidayId={selectedHoliday.id} 
                date={selectedDate} 
                activities={activities.filter(a => a.date === selectedDate)} 
              />
            )}
            {activeSubTab === 'students' && (
              <AttendanceSection 
                holidayId={selectedHoliday.id} 
                date={selectedDate} 
                type="student"
                items={students
                  .sort((a, b) => (a.className || '').localeCompare(b.className || '', 'he') || (a.fullName || '').localeCompare(b.fullName || '', 'he'))
                  .map(s => ({ 
                    id: s.id, 
                    name: s.fullName, 
                    info: s.className,
                    fatherName: s.fatherName,
                    fatherPhone: s.fatherPhone,
                    motherName: s.motherName,
                    motherPhone: s.motherPhone
                  }))}
                attendance={attendance.filter(a => a.date === selectedDate && a.targetType === 'student')}
                selectedHoliday={selectedHoliday}
              />
            )}
            {activeSubTab === 'staff' && (
              <StaffAttendanceSection 
                holidayId={selectedHoliday.id} 
                date={selectedDate}
                staff={staff}
                substitutes={substitutes}
                attendance={attendance.filter(a => a.date === selectedDate && a.targetType !== 'student')}
              />
            )}
            {activeSubTab === 'transport' && (
              <TransportSection 
                activities={activities.filter(a => a.date === selectedDate && a.type === 'out-of-school')}
                transportLines={transportLines}
                attendance={attendance.filter(a => a.date === selectedDate)}
                holidayId={selectedHoliday.id}
                date={selectedDate}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">ניהול חופשות</h1>
          <p className="text-slate-500">נהל חופשות, פעילויות ונוכחות</p>
        </div>
        <button 
          onClick={() => setIsAddingHoliday(true)}
          className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
        >
          <Plus size={20} />
          <span>הוסף חופשה</span>
        </button>
      </div>

      <AnimatePresence>
        {isAddingHoliday && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white p-6 rounded-2xl shadow-xl border border-slate-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">שם החופשה</label>
                <input 
                  type="text" 
                  value={newHoliday.name}
                  onChange={e => setNewHoliday({...newHoliday, name: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="למשל: חופשת פסח"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">תאריך תחילה</label>
                <input 
                  type="date" 
                  value={newHoliday.startDate}
                  onChange={e => setNewHoliday({...newHoliday, startDate: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">תאריך סיום</label>
                <input 
                  type="date" 
                  value={newHoliday.endDate}
                  onChange={e => setNewHoliday({...newHoliday, endDate: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">אחראי</label>
                <input 
                  type="text" 
                  value={newHoliday.responsible}
                  onChange={e => setNewHoliday({...newHoliday, responsible: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="שם האחראי"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setIsAddingHoliday(false)}
                className="px-6 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button 
                onClick={handleAddHoliday}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
              >
                שמור חופשה
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {holidays.map(holiday => (
          <motion.div
            layout
            key={holiday.id}
            onClick={() => setSelectedHoliday(holiday)}
            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                <Calendar size={24} />
              </div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('האם אתה בטוח שברצונך למחוק חופשה זו?')) {
                    deleteDoc(doc(db, 'holidays', holiday.id));
                  }
                }}
                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-1">{holiday.name}</h3>
            <p className="text-slate-500 text-sm mb-4">{holiday.startDate} עד {holiday.endDate}</p>
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-slate-600">
                <Users size={16} />
                <span className="text-sm">{holiday.responsible}</span>
              </div>
              <ChevronRight className="text-slate-300 group-hover:text-indigo-600 transition-colors" />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function HolidayDashboard({ holidayId, date, students, staff, attendance }: { 
  holidayId: string, 
  date: string, 
  students: Student[], 
  staff: StaffMember[], 
  attendance: HolidayAttendance[] 
}) {
  const stats = useMemo(() => {
    const studentAttendance = attendance.filter(a => a.targetType === 'student');
    const educationalStaff = staff.filter(s => s.type === 'educational');
    const assistantStaff = staff.filter(s => s.type === 'assistant');
    
    const educationalAttendance = attendance.filter(a => a.targetType === 'educational');
    const assistantAttendance = attendance.filter(a => a.targetType === 'assistant');

    const getStats = (total: number, present: number) => ({
      total,
      present,
      absent: total - present,
      percent: total > 0 ? Math.round((present / total) * 100) : 0
    });

    return {
      students: getStats(students.length, studentAttendance.filter(a => a.status === 'present').length),
      educational: getStats(educationalStaff.length, educationalAttendance.filter(a => a.status === 'present').length),
      assistants: getStats(assistantStaff.length, assistantAttendance.filter(a => a.status === 'present').length)
    };
  }, [students, staff, attendance]);

  const classStats = useMemo(() => {
    const studentAttendance = attendance.filter(a => a.targetType === 'student');
    const classes = Array.from(new Set(students.map(s => s.className))).sort((a, b) => (a || '').localeCompare(b || '', 'he'));
    
    return classes.map(className => {
      const classStudents = students.filter(s => s.className === className);
      const presentCount = classStudents.filter(s => 
        studentAttendance.find(a => a.targetId === s.id && a.status === 'present')
      ).length;
      
      return {
        name: className,
        total: classStudents.length,
        present: presentCount,
        percent: classStudents.length > 0 ? Math.round((presentCount / classStudents.length) * 100) : 0
      };
    });
  }, [students, attendance]);

  const StatCard = ({ title, data, color, icon: Icon }: any) => (
    <div className={`bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-600`}>
          <Icon size={24} />
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <h4 className="text-2xl font-bold text-slate-900">{data.total}</h4>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">נוכחות</span>
          <span className={`font-bold text-${color}-600`}>{data.percent}%</span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${data.percent}%` }}
            className={`h-full bg-${color}-500`}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          <div className="bg-emerald-50 p-2 rounded-lg text-center">
            <p className="text-[10px] text-emerald-600 font-bold uppercase">נוכחים</p>
            <p className="text-lg font-bold text-emerald-700">{data.present}</p>
          </div>
          <div className="bg-red-50 p-2 rounded-lg text-center">
            <p className="text-[10px] text-red-600 font-bold uppercase">נעדרים</p>
            <p className="text-lg font-bold text-red-700">{data.absent}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title="תלמידים" data={stats.students} color="indigo" icon={Users} />
        <StatCard title="צוות חינוכי/טיפולי" data={stats.educational} color="emerald" icon={Clock} />
        <StatCard title="סייעות" data={stats.assistants} color="orange" icon={Clock} />
      </div>

      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={20} className="text-indigo-600" />
            סטטוס נוכחות לפי קבוצות
          </div>
          <div className="bg-white px-3 py-1 rounded-full border border-slate-200 text-xs font-bold text-slate-500">
            סה"כ נוכחות כללית: {Math.round((stats.students.present + stats.educational.present + stats.assistants.present) / 
                (stats.students.total + stats.educational.total + stats.assistants.total || 1) * 100)}%
          </div>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {classStats.map(c => (
            <div key={c.name} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
              <div className="flex justify-between items-start mb-2">
                <h5 className="font-bold text-slate-800 text-sm">{c.name}</h5>
                <span className={`text-xs font-bold ${c.percent > 80 ? 'text-emerald-600' : c.percent > 50 ? 'text-orange-500' : 'text-red-500'}`}>
                  {c.percent}%
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div className={`h-full ${c.percent > 80 ? 'bg-emerald-500' : c.percent > 50 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${c.percent}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-slate-400 font-medium">
                <span>נוכחים: {c.present}</span>
                <span>סה"כ: {c.total}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActivitySection({ holidayId, date, activities }: { holidayId: string, date: string, activities: HolidayActivity[] }) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newActivity, setNewActivity] = useState<Partial<HolidayActivity>>({
    type: 'in-school',
    activityName: '',
    operator: '',
    transporterName: '',
    transportCount: 1
  });

  const handleSave = async () => {
    if (!newActivity.activityName) return;
    
    if (editingId) {
      await updateDoc(doc(db, 'holidayActivities', editingId), {
        ...newActivity
      });
    } else {
      await addDoc(collection(db, 'holidayActivities'), {
        ...newActivity,
        holidayId,
        date
      });
    }
    
    setIsAdding(false);
    setEditingId(null);
    setNewActivity({ type: 'in-school', activityName: '', operator: '', transporterName: '', transportCount: 1 });
  };

  const startEdit = (activity: HolidayActivity) => {
    setNewActivity({
      type: activity.type,
      activityName: activity.activityName,
      operator: activity.operator,
      transporterName: activity.transporterName,
      transportCount: activity.transportCount
    });
    setEditingId(activity.id);
    setIsAdding(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-slate-800">פעילויות ליום זה</h3>
        <button 
          onClick={() => {
            setIsAdding(true);
            setEditingId(null);
            setNewActivity({ type: 'in-school', activityName: '', operator: '', transporterName: '', transportCount: 1 });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
        >
          <Plus size={18} />
          <span>הוסף פעילות</span>
        </button>
      </div>

      {isAdding && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">סוג פעילות</label>
              <select 
                value={newActivity.type}
                onChange={e => setNewActivity({...newActivity, type: e.target.value as any})}
                className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none"
              >
                <option value="in-school">תוך בית ספרית</option>
                <option value="out-of-school">חוץ בית ספרית</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">שם הפעילות</label>
              <input 
                type="text" 
                value={newActivity.activityName}
                onChange={e => setNewActivity({...newActivity, activityName: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">מפעיל</label>
              <input 
                type="text" 
                value={newActivity.operator}
                onChange={e => setNewActivity({...newActivity, operator: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none"
              />
            </div>
            {newActivity.type === 'out-of-school' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">שם המסיע</label>
                  <input 
                    type="text" 
                    value={newActivity.transporterName}
                    onChange={e => setNewActivity({...newActivity, transporterName: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">כמות הסעות</label>
                  <input 
                    type="number" 
                    value={newActivity.transportCount}
                    onChange={e => setNewActivity({...newActivity, transportCount: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none"
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setIsAdding(false); setEditingId(null); }} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg">ביטול</button>
            <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              {editingId ? 'עדכן' : 'שמור'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {activities.map(activity => (
          <div key={activity.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl">
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${activity.type === 'in-school' ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
                {activity.type === 'in-school' ? <Activity size={20} /> : <Bus size={20} />}
              </div>
              <div>
                <h4 className="font-bold text-slate-800">{activity.activityName}</h4>
                <p className="text-sm text-slate-500">מפעיל: {activity.operator} | {activity.type === 'in-school' ? 'תוך בית ספרית' : 'חוץ בית ספרית'}</p>
                {activity.type === 'out-of-school' && (
                  <p className="text-sm text-indigo-600 font-medium">מסיע: {activity.transporterName} ({activity.transportCount} הסעות)</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => startEdit(activity)}
                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
              >
                <Edit2 size={18} />
              </button>
              <button 
                onClick={() => deleteDoc(doc(db, 'holidayActivities', activity.id))}
                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
        {activities.length === 0 && !isAdding && (
          <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
            <Activity className="mx-auto text-slate-300 mb-2" size={48} />
            <p className="text-slate-500">אין פעילויות מתוכננות ליום זה</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AttendanceSection({ holidayId, date, type, items, attendance, selectedHoliday }: { 
  holidayId: string, 
  date: string, 
  type: string, 
  items: { id: string, name: string, info: string }[],
  attendance: HolidayAttendance[],
  selectedHoliday: Holiday | null
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [warningStudent, setWarningStudent] = useState<{ student: any, days: number } | null>(null);

  const checkConsecutiveAbsences = (studentId: string, newStatus: string) => {
    if (newStatus !== 'absent') return 0;

    const holidayDates = getDaysArray(selectedHoliday?.startDate || '', selectedHoliday?.endDate || '').sort();
    const currentIndex = holidayDates.indexOf(date);
    if (currentIndex === -1) return 0;

    let count = 1; // Current day is absent
    
    // Check previous days
    for (let i = currentIndex - 1; i >= 0; i--) {
      const d = holidayDates[i];
      const record = attendance.find(a => a.targetId === studentId && a.date === d && a.targetType === 'student');
      if (record?.status === 'absent') {
        count++;
      } else {
        break;
      }
    }
    return count;
  };

  const toggleStatus = async (itemId: string, currentStatus: string | undefined) => {
    // Default is now 'absent' if no record exists
    const effectiveStatus = currentStatus || 'absent';
    const newStatus = effectiveStatus === 'absent' ? 'present' : 'absent';
    
    if (newStatus === 'absent' && type === 'student') {
      const consecutiveDays = checkConsecutiveAbsences(itemId, newStatus);
      if (consecutiveDays >= 3) {
        const student = items.find(i => i.id === itemId);
        if (student) {
          // We need the full student data for phone numbers
          // Since we only have 'items' here, we might need to pass them or fetch them
          // But 'items' already has name and info. We can pass the full student list to the section.
          setWarningStudent({ 
            student: { ...student }, 
            days: consecutiveDays 
          });
        }
      }
    }

    const docId = `${holidayId}_${date}_${itemId}`;
    
    await setDoc(doc(db, 'holidayAttendance', docId), {
      holidayId,
      date,
      targetId: itemId,
      targetType: type,
      status: newStatus,
      lastUpdated: serverTimestamp()
    }, { merge: true });
    
    // Sync to regular attendance
    await syncHolidayToRegular(itemId, type, date, newStatus);
  };

  const setAllStatus = async (status: 'present' | 'absent') => {
    setSyncing(true);
    try {
      for (const item of items) {
        const docId = `${holidayId}_${date}_${item.id}`;
        await setDoc(doc(db, 'holidayAttendance', docId), {
          holidayId,
          date,
          targetId: item.id,
          targetType: type,
          status,
          lastUpdated: serverTimestamp()
        }, { merge: true });
        
        // Sync to regular attendance
        await syncHolidayToRegular(item.id, type, date, status);
      }
    } finally {
      setSyncing(false);
    }
  };

  const forceSyncAll = async () => {
    setSyncing(true);
    try {
      for (const item of items) {
        const record = attendanceMap[item.id];
        const status = record?.status || 'absent'; // Default to absent if not reported
        await syncHolidayToRegular(item.id, type, date, status as any);
      }
      alert('הסנכרון הושלם בהצלחה!');
    } catch (error) {
      console.error("Error during force sync:", error);
      alert('שגיאה במהלך הסנכרון');
    } finally {
      setSyncing(false);
    }
  };

  const filteredItems = items.filter(i => i.name.includes(searchTerm) || i.info.includes(searchTerm));
  
  // Use a map to handle potential duplicate records from previous bugs
  const attendanceMap = useMemo(() => {
    const map: Record<string, HolidayAttendance> = {};
    attendance.forEach(a => {
      const deterministicId = `${holidayId}_${date}_${a.targetId}`;
      if (!map[a.targetId] || a.id === deterministicId) {
        map[a.targetId] = a;
      }
    });
    return map;
  }, [attendance, holidayId, date]);

  const presentItems = filteredItems.filter(item => attendanceMap[item.id]?.status === 'present');
  const absentItems = filteredItems.filter(item => !attendanceMap[item.id] || attendanceMap[item.id]?.status === 'absent');

  // Calculate counts based on unique items
  const presentCount = items.filter(item => attendanceMap[item.id]?.status === 'present').length;
  const absentCount = items.filter(item => !attendanceMap[item.id] || attendanceMap[item.id]?.status === 'absent').length;

  return (
    <div className="space-y-6">
      {/* Warning Modal for Consecutive Absences */}
      <AnimatePresence>
        {warningStudent && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-red-100"
            >
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                  <AlertTriangle size={40} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">התראת היעדרות ממושכת</h3>
                <p className="text-slate-600 mb-6">
                  התלמיד/ה <span className="font-bold text-red-600">{warningStudent.student.name}</span> נעדר/ת כבר <span className="font-bold text-red-600">{warningStudent.days}</span> ימים ברצף!
                </p>
                
                <div className="bg-slate-50 rounded-2xl p-4 mb-8 text-right space-y-3 border border-slate-100">
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">פרטי קשר הורים:</p>
                  {(warningStudent.student as any).fatherName && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-700 font-medium">{(warningStudent.student as any).fatherName} (אבא):</span>
                      <a href={`tel:${(warningStudent.student as any).fatherPhone}`} className="text-indigo-600 font-bold hover:underline flex items-center gap-1">
                        <Phone size={14} />
                        {(warningStudent.student as any).fatherPhone}
                      </a>
                    </div>
                  )}
                  {(warningStudent.student as any).motherName && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-700 font-medium">{(warningStudent.student as any).motherName} (אמא):</span>
                      <a href={`tel:${(warningStudent.student as any).motherPhone}`} className="text-indigo-600 font-bold hover:underline flex items-center gap-1">
                        <Phone size={14} />
                        {(warningStudent.student as any).motherPhone}
                      </a>
                    </div>
                  )}
                  {!(warningStudent.student as any).fatherPhone && !(warningStudent.student as any).motherPhone && (
                    <p className="text-slate-400 italic text-sm">לא הוזנו מספרי טלפון להורים</p>
                  )}
                </div>

                <button
                  onClick={() => setWarningStudent(null)}
                  className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl hover:bg-slate-800 transition-all shadow-lg"
                >
                  הבנתי, המשך
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-4 text-sm font-medium">
            <span className="text-slate-500">סה"כ: <span className="text-slate-900 font-bold">{items.length}</span></span>
            <span className="text-emerald-600">נוכחים: <span className="font-bold">{presentCount}</span></span>
            <span className="text-red-600">נעדרים: <span className="font-bold">{absentCount}</span></span>
          </div>
          
          <div className="h-8 w-px bg-slate-200 hidden lg:block"></div>

          <div className="flex gap-2">
            <button 
              onClick={() => setAllStatus('present')}
              disabled={syncing}
              className="px-4 py-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-all font-bold text-sm disabled:opacity-50"
            >
              סמן הכל כנוכח
            </button>
            <button 
              onClick={() => setAllStatus('absent')}
              disabled={syncing}
              className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all font-bold text-sm disabled:opacity-50"
            >
              סמן הכל כנעדר
            </button>
            <button 
              onClick={forceSyncAll}
              disabled={syncing}
              title="סנכרן מחדש את כל הנתונים ללשונית הנוכחות הרגילה"
              className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {syncing ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              סנכרן נתונים
            </button>
            {showSaved && (
              <span className="text-emerald-600 text-xs font-bold animate-in fade-in slide-in-from-right-2">נשמר!</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 w-full lg:w-72">
          <Search className="text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="חפש תלמיד..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="bg-transparent border-none outline-none w-full text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Right Column: Present */}
        <div className="space-y-4">
          <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2">
            <UserCheck size={20} className="text-emerald-600" />
            נוכחים ({presentItems.length})
          </h4>
          <div className="grid grid-cols-1 gap-3">
            {presentItems.map(item => {
              const record = attendanceMap[item.id];
              const status = record?.status;
              
              return (
                <div 
                  key={item.id}
                  onClick={() => toggleStatus(item.id, status)}
                  className={`p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between bg-white border-slate-200 hover:border-indigo-300 shadow-sm`}
                >
                  <div>
                    <h4 className="font-bold text-slate-800">{item.name}</h4>
                    <p className="text-sm text-slate-500">{item.info}</p>
                  </div>
                  <div className={`p-2 rounded-full bg-emerald-500 text-white`}>
                    <Check size={16} />
                  </div>
                </div>
              );
            })}
            {presentItems.length === 0 && (
              <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                אין נוכחים מדווחים
              </div>
            )}
          </div>
        </div>

        {/* Left Column: Absent / Unreported */}
        <div className="space-y-4">
          <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2">
            <UserX size={20} className="text-red-600" />
            נעדרים / טרם דווחו ({absentItems.length})
          </h4>
          <div className="grid grid-cols-1 gap-3">
            {absentItems.map(item => {
              const record = attendanceMap[item.id];
              const status = record?.status;
              
              return (
                <div 
                  key={item.id}
                  onClick={() => toggleStatus(item.id, status)}
                  className={`p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between bg-red-50 border-red-200 shadow-sm`}
                >
                  <div>
                    <h4 className="font-bold text-slate-800">{item.name}</h4>
                    <p className="text-sm text-slate-500">{item.info}</p>
                  </div>
                  <div className={`p-2 rounded-full bg-slate-100 text-slate-400`}>
                    <X size={16} />
                  </div>
                </div>
              );
            })}
            {absentItems.length === 0 && (
              <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                אין נעדרים ברשימה זו
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffAttendanceSection({ holidayId, date, staff, substitutes, attendance }: {
  holidayId: string,
  date: string,
  staff: StaffMember[],
  substitutes: Substitute[],
  attendance: HolidayAttendance[]
}) {
  const [defaultHours, setDefaultHours] = useState<number>(5);
  const [activeCategory, setActiveCategory] = useState<'educational' | 'assistant' | 'substitute'>('educational');
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const applyDefaultHours = async () => {
    setSyncing(true);
    try {
      const currentCategoryItems = activeCategory === 'substitute' 
        ? substitutes 
        : staff.filter(s => s.type === activeCategory);

      for (const item of currentCategoryItems) {
        const type = activeCategory === 'substitute' ? 'substitute' : activeCategory;
        const docId = `${holidayId}_${date}_${item.id}`;
        await setDoc(doc(db, 'holidayAttendance', docId), {
          holidayId,
          date,
          targetId: item.id,
          targetType: type,
          status: 'present',
          hours: defaultHours,
          lastUpdated: serverTimestamp()
        }, { merge: true });
        
        // Sync to regular attendance
        await syncHolidayToRegular(item.id, type, date, 'present');
      }
    } finally {
      setSyncing(false);
    }
  };

  const updateHours = async (itemId: string, hours: number, type: string) => {
    const docId = `${holidayId}_${date}_${itemId}`;
    await setDoc(doc(db, 'holidayAttendance', docId), {
      holidayId,
      date,
      targetId: itemId,
      targetType: type,
      status: 'present',
      hours,
      lastUpdated: serverTimestamp()
    }, { merge: true });
    
    // Sync to regular attendance
    await syncHolidayToRegular(itemId, type, date, 'present');
  };

  const toggleStatus = async (itemId: string, currentStatus: string | undefined, type: string) => {
    const newStatus = currentStatus === 'present' ? 'absent' : 'present';
    const docId = `${holidayId}_${date}_${itemId}`;
    
    await setDoc(doc(db, 'holidayAttendance', docId), {
      holidayId,
      date,
      targetId: itemId,
      targetType: type,
      status: newStatus,
      hours: newStatus === 'present' ? defaultHours : 0,
      lastUpdated: serverTimestamp()
    }, { merge: true });
    
    // Sync to regular attendance
    await syncHolidayToRegular(itemId, type, date, newStatus);
  };

  const setAllStatus = async (status: 'present' | 'absent') => {
    setSyncing(true);
    try {
      const currentCategoryItems = activeCategory === 'substitute' 
        ? substitutes 
        : staff.filter(s => s.type === activeCategory);

      for (const item of currentCategoryItems) {
        const type = activeCategory === 'substitute' ? 'substitute' : activeCategory;
        const docId = `${holidayId}_${date}_${item.id}`;
        await setDoc(doc(db, 'holidayAttendance', docId), {
          holidayId,
          date,
          targetId: item.id,
          targetType: type,
          status,
          hours: status === 'present' ? defaultHours : 0,
          lastUpdated: serverTimestamp()
        }, { merge: true });
        
        // Sync to regular attendance
        await syncHolidayToRegular(item.id, type, date, status);
      }
    } finally {
      setSyncing(false);
    }
  };

  const forceSyncAll = async () => {
    setSyncing(true);
    try {
      const currentCategoryItems = activeCategory === 'substitute' 
        ? substitutes 
        : staff.filter(s => s.type === activeCategory);

      for (const item of currentCategoryItems) {
        const record = attendance.find(a => a.targetId === item.id);
        const status = record?.status || 'absent';
        await syncHolidayToRegular(item.id, activeCategory === 'substitute' ? 'substitute' : activeCategory, date, status as any);
      }
      alert('הסנכרון הושלם בהצלחה!');
    } catch (error) {
      console.error("Error during force sync:", error);
      alert('שגיאה במהלך הסנכרון');
    } finally {
      setSyncing(false);
    }
  };

  const allCurrentItems = useMemo(() => {
    if (activeCategory === 'substitute') {
      // Unify substitutes from both collections
      const fromSubsColl = substitutes.map(s => ({ 
        id: s.id, 
        name: `${s.firstName} ${s.lastName}`, 
        info: 'מילוי מקום חיצוני', 
        type: 'substitute' as const 
      }));
      
      const fromStaffColl = staff
        .filter(s => s.isSubstitute)
        .map(s => ({ 
          id: s.id, 
          name: s.fullName, 
          info: s.type === 'educational' ? 'חינוכי' : 'סייע/ת', 
          type: 'substitute' as const 
        }));

      // Merge and remove duplicates by ID
      const merged = [...fromSubsColl, ...fromStaffColl];
      const unique = Array.from(new Map(merged.map(item => [item.id, item])).values());
      return unique.sort((a, b) => a.name.localeCompare(b.name, 'he'));
    }
    
    return staff
      .filter(s => s.type === activeCategory)
      .map(s => ({ id: s.id, name: s.fullName, info: s.role || '', type: s.type }))
      .sort((a, b) => a.name.localeCompare(b.name, 'he'));
  }, [activeCategory, staff, substitutes]);

  const filteredItems = allCurrentItems.filter(i => i.name.includes(searchTerm) || i.info.includes(searchTerm));

  // Use a map to handle potential duplicate records
  const attendanceMap = useMemo(() => {
    const map: Record<string, HolidayAttendance> = {};
    attendance.forEach(a => {
      const deterministicId = `${holidayId}_${date}_${a.targetId}`;
      if (!map[a.targetId] || a.id === deterministicId) {
        map[a.targetId] = a;
      }
    });
    return map;
  }, [attendance, holidayId, date]);

  const presentItems = filteredItems.filter(item => attendanceMap[item.id]?.status === 'present');
  const absentItems = filteredItems.filter(item => !attendanceMap[item.id] || attendanceMap[item.id]?.status === 'absent');

  // Calculate counts based on unique items
  const presentCount = allCurrentItems.filter(item => attendanceMap[item.id]?.status === 'present').length;
  const absentCount = allCurrentItems.filter(item => !attendanceMap[item.id] || attendanceMap[item.id]?.status === 'absent').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-4 text-sm font-medium">
            <span className="text-slate-500">סה"כ: <span className="text-slate-900 font-bold">{allCurrentItems.length}</span></span>
            <span className="text-emerald-600">נוכחים: <span className="font-bold">{presentCount}</span></span>
            <span className="text-red-600">נעדרים: <span className="font-bold">{absentCount}</span></span>
          </div>
          
          <div className="h-8 w-px bg-slate-200 hidden lg:block"></div>

          <div className="flex gap-2">
            <button 
              onClick={() => setAllStatus('present')}
              disabled={syncing}
              className="px-4 py-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-all font-bold text-sm disabled:opacity-50"
            >
              סמן הכל כנוכח
            </button>
            <button 
              onClick={() => setAllStatus('absent')}
              disabled={syncing}
              className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all font-bold text-sm disabled:opacity-50"
            >
              סמן הכל כנעדר
            </button>
            <button 
              onClick={forceSyncAll}
              disabled={syncing}
              className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {syncing ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              סנכרן נתונים
            </button>
            {showSaved && (
              <span className="text-emerald-600 text-xs font-bold animate-in fade-in slide-in-from-right-2">נשמר!</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 w-full lg:w-72">
          <Search className="text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="חפש איש צוות..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="bg-transparent border-none outline-none w-full text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
          {[
            { id: 'educational', label: 'צוות חינוכי טיפולי' },
            { id: 'assistant', label: 'סייעות' },
            { id: 'substitute', label: 'מילוי מקום' },
          ].map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id as any)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeCategory === cat.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-slate-200">
          <span className="text-sm text-slate-600">שעות ברירת מחדל:</span>
          <input 
            type="number" 
            value={defaultHours}
            onChange={e => setDefaultHours(parseInt(e.target.value))}
            className="w-16 px-2 py-1 border border-slate-200 rounded-lg text-center outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button 
            onClick={applyDefaultHours}
            disabled={syncing}
            className="px-4 py-1 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            החל על כולם
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Right Column: Present/Unreported */}
        <div className="space-y-4">
          <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2">
            <UserCheck size={20} className="text-emerald-600" />
            נוכחים ({presentItems.length})
          </h4>
          <div className="grid grid-cols-1 gap-3">
            {presentItems.map(item => {
              const record = attendanceMap[item.id];
              const status = record?.status;
              const hours = record?.hours || 0;
              
              return (
                <div 
                  key={item.id}
                  className={`p-4 rounded-xl border transition-all flex flex-col gap-3 bg-white border-slate-200 shadow-sm`}
                >
                  <div className="flex items-center justify-between">
                    <div onClick={() => toggleStatus(item.id, status, item.type)} className="cursor-pointer">
                      <h4 className="font-bold text-slate-800">{item.name}</h4>
                      <p className="text-sm text-slate-500">{item.info}</p>
                    </div>
                    <div 
                      onClick={() => toggleStatus(item.id, status, item.type)}
                      className={`p-2 rounded-full cursor-pointer bg-slate-100 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 transition-colors`}
                    >
                      <Check size={16} />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <Clock size={16} className="text-slate-400" />
                    <span className="text-sm text-slate-600">שעות עבודה:</span>
                    <input 
                      type="number" 
                      value={hours}
                      onChange={e => updateHours(item.id, parseInt(e.target.value), item.type)}
                      className="w-16 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-center text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              );
            })}
            {presentItems.length === 0 && (
              <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                אין אנשי צוות ברשימה זו
              </div>
            )}
          </div>
        </div>

        {/* Left Column: Absent */}
        <div className="space-y-4">
          <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2">
            <UserX size={20} className="text-red-600" />
            נעדרים / טרם דווחו ({absentItems.length})
          </h4>
          <div className="grid grid-cols-1 gap-3">
            {absentItems.map(item => {
              const record = attendanceMap[item.id];
              const status = record?.status;
              
              return (
                <div 
                  key={item.id}
                  className={`p-4 rounded-xl border transition-all flex flex-col gap-3 bg-red-50 border-red-200 shadow-sm`}
                >
                  <div className="flex items-center justify-between">
                    <div onClick={() => toggleStatus(item.id, status, item.type)} className="cursor-pointer">
                      <h4 className="font-bold text-slate-800">{item.name}</h4>
                      <p className="text-sm text-slate-500">{item.info}</p>
                    </div>
                    <div 
                      onClick={() => toggleStatus(item.id, status, item.type)}
                      className={`p-2 rounded-full cursor-pointer bg-red-500 text-white shadow-lg shadow-red-100`}
                    >
                      <X size={16} />
                    </div>
                  </div>
                </div>
              );
            })}
            {absentItems.length === 0 && (
              <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                אין נעדרים מדווחים
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TransportSection({ activities, transportLines, attendance, holidayId, date }: { 
  activities: HolidayActivity[], 
  transportLines: TransportLine[], 
  attendance: HolidayAttendance[],
  holidayId: string,
  date: string
}) {
  const [syncing, setSyncing] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const externalTransporters = Array.from(new Set(activities.map(a => a.transporterName).filter(Boolean)));

  const toggleStatus = async (targetId: string, currentStatus: string | undefined, type: 'transporter' | 'transportLine') => {
    const newStatus = currentStatus === 'present' ? 'absent' : 'present';
    const docId = `${holidayId}_${date}_${targetId}`;
    
    await setDoc(doc(db, 'holidayAttendance', docId), {
      holidayId,
      date,
      targetId,
      targetType: type,
      status: newStatus,
      lastUpdated: serverTimestamp()
    }, { merge: true });
    
    // Sync to regular attendance using batch for robustness
    const displayName = type === 'transportLine' ? (transportLines.find(l => l.id === targetId)?.name || targetId) : targetId;
    await syncHolidayTransportBatchToRegular(date, [{
      targetId,
      targetType: type,
      status: newStatus,
      displayName
    }]);
  };

  const setAllStatus = async (type: 'transporter' | 'transportLine', status: 'present' | 'absent') => {
    setSyncing(true);
    try {
      const items = type === 'transportLine' ? transportLines : externalTransporters.map(name => ({ id: name!, name: name! }));
      const updates = [];
      const { writeBatch } = await import('firebase/firestore');
      const batch = writeBatch(db);

      for (const item of items) {
        const id = typeof item === 'string' ? item : item.id;
        const name = typeof item === 'string' ? item : item.name;
        if (!id) continue;
        const docId = `${holidayId}_${date}_${id}`;
        
        batch.set(doc(db, 'holidayAttendance', docId), {
          holidayId,
          date,
          targetId: id,
          targetType: type,
          status,
          lastUpdated: serverTimestamp()
        }, { merge: true });
        
        updates.push({ 
          targetId: id, 
          targetType: type, 
          status, 
          displayName: name 
        });
      }
      
      await batch.commit();
      
      // Sync to regular attendance in batch
      await syncHolidayTransportBatchToRegular(date, updates);
    } finally {
      setSyncing(false);
    }
  };

  const forceSyncAll = async () => {
    setSyncing(true);
    try {
      const updates: any[] = [];
      
      // Regular Lines
      transportLines.forEach(line => {
        const record = attendanceMap[line.id];
        updates.push({
          targetId: line.id,
          targetType: 'transportLine',
          status: record?.status || 'absent',
          displayName: line.name
        });
      });
      
      // External Transporters
      externalTransporters.forEach(name => {
        if (!name) return;
        const record = attendanceMap[name];
        updates.push({
          targetId: name,
          targetType: 'transporter',
          status: record?.status || 'absent',
          displayName: name
        });
      });
      
      await syncHolidayTransportBatchToRegular(date, updates);
    } catch (error) {
      console.error("Error during force sync:", error);
      alert('שגיאה במהלך הסנכרון');
    } finally {
      setSyncing(false);
    }
  };

  // Use a map to handle potential duplicate records
  const attendanceMap = useMemo(() => {
    const map: Record<string, HolidayAttendance> = {};
    attendance.forEach(a => {
      const deterministicId = `${holidayId}_${date}_${a.targetId}`;
      if (!map[a.targetId] || a.id === deterministicId) {
        map[a.targetId] = a;
      }
    });
    return map;
  }, [attendance, holidayId, date]);

  const presentLines = transportLines.filter(line => attendanceMap[line.id]?.status === 'present');
  const absentLines = transportLines.filter(line => !attendanceMap[line.id] || attendanceMap[line.id]?.status === 'absent');

  const presentTransporters = externalTransporters.filter(t => attendanceMap[t!]?.status === 'present');
  const absentTransporters = externalTransporters.filter(t => !attendanceMap[t!] || attendanceMap[t!]?.status === 'absent');

  // Calculate counts based on unique items to avoid duplicate record issues
  const linePresentCount = transportLines.filter(line => 
    attendanceMap[line.id]?.status === 'present'
  ).length;
  
  const lineAbsentCount = transportLines.filter(line => 
    !attendanceMap[line.id] || attendanceMap[line.id]?.status === 'absent'
  ).length;
  
  const transporterPresentCount = externalTransporters.filter(name => 
    attendanceMap[name!]?.status === 'present'
  ).length;
  
  const transporterAbsentCount = externalTransporters.filter(name => 
    !attendanceMap[name!] || attendanceMap[name!]?.status === 'absent'
  ).length;

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex flex-wrap items-center gap-6">
            <h3 className="text-lg font-bold text-slate-800">נוכחות קווי הסעה (רגילים)</h3>
            <div className="flex items-center gap-4 text-sm font-medium">
              <span className="text-slate-500">סה"כ: <span className="text-slate-900 font-bold">{transportLines.length}</span></span>
              <span className="text-emerald-600">נוכחים: <span className="font-bold">{linePresentCount}</span></span>
              <span className="text-red-600">נעדרים: <span className="font-bold">{lineAbsentCount}</span></span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button 
              onClick={() => setAllStatus('transportLine', 'present')}
              disabled={syncing}
              className="px-4 py-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-all font-bold text-sm disabled:opacity-50"
            >
              סמן הכל כנוכח
            </button>
            <button 
              onClick={() => setAllStatus('transportLine', 'absent')}
              disabled={syncing}
              className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all font-bold text-sm disabled:opacity-50"
            >
              סמן הכל כנעדר
            </button>
            <button 
              onClick={() => forceSyncAll()}
              disabled={syncing}
              className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {syncing ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              סנכרן
            </button>
            {showSaved && (
              <span className="text-emerald-600 text-xs font-bold animate-in fade-in slide-in-from-right-2">נשמר!</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Right Column: Present/Unreported */}
          <div className="space-y-4">
            <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2 text-sm">
              <UserCheck size={18} className="text-emerald-600" />
              קווים נוכחים ({presentLines.length})
            </h4>
            <div className="grid grid-cols-1 gap-3">
              {presentLines.map(line => {
                const record = attendanceMap[line.id];
                const status = record?.status;
                
                return (
                  <div 
                    key={line.id}
                    onClick={() => toggleStatus(line.id, status, 'transportLine')}
                    className={`p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between bg-white border-slate-200 hover:border-indigo-300 shadow-sm`}
                  >
                    <div>
                      <h4 className="font-bold text-slate-800">{line.name}</h4>
                      <p className="text-sm text-slate-500">{line.driverName || 'ללא שם נהג'}</p>
                    </div>
                    <div className={`p-2 rounded-full bg-slate-100 text-slate-400`}>
                      <Check size={16} />
                    </div>
                  </div>
                );
              })}
              {presentLines.length === 0 && (
                <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                  אין קווים ברשימה זו
                </div>
              )}
            </div>
          </div>

          {/* Left Column: Absent */}
          <div className="space-y-4">
            <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2 text-sm">
              <UserX size={18} className="text-red-600" />
              קווים נעדרים ({absentLines.length})
            </h4>
            <div className="grid grid-cols-1 gap-3">
              {absentLines.map(line => {
                const record = attendanceMap[line.id];
                const status = record?.status;
                
                return (
                  <div 
                    key={line.id}
                    onClick={() => toggleStatus(line.id, status, 'transportLine')}
                    className={`p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between bg-red-50 border-red-200 shadow-sm`}
                  >
                    <div>
                      <h4 className="font-bold text-slate-800">{line.name}</h4>
                      <p className="text-sm text-slate-500">{line.driverName || 'ללא שם נהג'}</p>
                    </div>
                    <div className={`p-2 rounded-full bg-red-500 text-white`}>
                      <X size={16} />
                    </div>
                  </div>
                );
              })}
              {absentLines.length === 0 && (
                <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                  אין קווים נעדרים
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {externalTransporters.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex flex-wrap items-center gap-6">
              <h3 className="text-lg font-bold text-slate-800">מסיעים חיצוניים (לפעילות חוץ)</h3>
              <div className="flex items-center gap-4 text-sm font-medium">
                <span className="text-slate-500">סה"כ: <span className="text-slate-900 font-bold">{externalTransporters.length}</span></span>
                <span className="text-emerald-600">נוכחים: <span className="font-bold">{transporterPresentCount}</span></span>
                <span className="text-red-600">נעדרים: <span className="font-bold">{transporterAbsentCount}</span></span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={() => setAllStatus('transporter', 'present')}
                disabled={syncing}
                className="px-4 py-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-all font-bold text-sm disabled:opacity-50"
              >
                סמן הכל כנוכח
              </button>
              <button 
                onClick={() => setAllStatus('transporter', 'absent')}
                disabled={syncing}
                className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all font-bold text-sm disabled:opacity-50"
              >
                סמן הכל כנעדר
              </button>
              <button 
                onClick={() => forceSyncAll()}
                disabled={syncing}
                className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all font-bold text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {syncing ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                סנכרן
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Right Column: Present/Unreported */}
            <div className="space-y-4">
              <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2 text-sm">
                <UserCheck size={18} className="text-emerald-600" />
                מסיעים נוכחים ({presentTransporters.length})
              </h4>
              <div className="grid grid-cols-1 gap-3">
                {presentTransporters.map(name => {
                  const record = attendanceMap[name!];
                  const status = record?.status;
                  
                  return (
                    <div 
                      key={name}
                      onClick={() => toggleStatus(name!, status, 'transporter')}
                      className={`p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between bg-white border-slate-200 hover:border-indigo-300 shadow-sm`}
                    >
                      <div>
                        <h4 className="font-bold text-slate-800">{name}</h4>
                        <p className="text-sm text-slate-500">מסיע חיצוני</p>
                      </div>
                      <div className={`p-2 rounded-full bg-slate-100 text-slate-400`}>
                        <Check size={16} />
                      </div>
                    </div>
                  );
                })}
                {presentTransporters.length === 0 && (
                  <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                    אין מסיעים ברשימה זו
                  </div>
                )}
              </div>
            </div>

            {/* Left Column: Absent */}
            <div className="space-y-4">
              <h4 className="font-bold text-slate-700 flex items-center gap-2 px-2 text-sm">
                <UserX size={18} className="text-red-600" />
                מסיעים נעדרים ({absentTransporters.length})
              </h4>
              <div className="grid grid-cols-1 gap-3">
                {absentTransporters.map(name => {
                  const record = attendanceMap[name!];
                  const status = record?.status;
                  
                  return (
                    <div 
                      key={name}
                      onClick={() => toggleStatus(name!, status, 'transporter')}
                      className={`p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between bg-red-50 border-red-200 shadow-sm`}
                    >
                      <div>
                        <h4 className="font-bold text-slate-800">{name}</h4>
                        <p className="text-sm text-slate-500">מסיע חיצוני</p>
                      </div>
                      <div className={`p-2 rounded-full bg-red-500 text-white`}>
                        <X size={16} />
                      </div>
                    </div>
                  );
                })}
                {absentTransporters.length === 0 && (
                  <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
                    אין מסיעים נעדרים
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
